import { JSX } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    TableContainer,
    IconButton,
    Skeleton,
    Tooltip,
    Typography
} from '@mui/material';
import { useTokenHistoryTimingData, TIMINGS_PAGE_SIZE } from "./hooks/token-history-graph.hooks";
import { TokenHistoryTiming } from "../../../types/dashboard";
import ReplayIcon from '@mui/icons-material/Replay';
import { TokenHistoryTimingTableRow } from "./components/TokenHistoryTimingTableRow";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DashboardCard, DashboardCardError } from "./DashboardCard";
import { numeric, eyebrow, formatTokens } from "./styles/dashboard-tokens";
import { slowestDuration } from "./timing-metrics";

export const TokenHistoryTimings: () => JSX.Element = (): JSX.Element => {
    const { data, loading, error, totalItems, reloadData, nextPage, previousPage, pageNum } = useTokenHistoryTimingData();

    function handleRefresh() {
        reloadData();
    }

    function handleNextPage() {
        nextPage();
    }

    function handlePreviousPage() {
        previousPage();
    }

    // Page size comes from the hook so this and nextPage() cannot drift apart.
    const pageTotal: number = Math.ceil(totalItems / TIMINGS_PAGE_SIZE);
    const timings: TokenHistoryTiming[] = data?.timings ?? [];
    const slowest: number = slowestDuration(timings);

    const actions: JSX.Element = (
        <Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5 } }>
            { (totalItems > TIMINGS_PAGE_SIZE) &&
                <>
                    <Tooltip title="Previous page">
                        <span>
                            <IconButton
                                aria-label="Previous page"
                                size="small"
                                disabled={ pageNum <= 1 }
                                onClick={ handlePreviousPage }
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography
                        component="p"
                        sx={ { ...numeric, fontSize: 12, color: 'text.secondary', minWidth: 56, textAlign: 'center' } }
                    >
                        { pageNum + ' / ' + pageTotal }
                    </Typography>
                    <Tooltip title="Next page">
                        <span>
                            <IconButton
                                aria-label="Next page"
                                size="small"
                                disabled={ pageNum >= pageTotal }
                                onClick={ handleNextPage }
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            }
            <Tooltip title="Reload execution times">
                <IconButton
                    aria-label="Reload execution times"
                    size="small"
                    sx={ { ml: 0.5 } }
                    onClick={ handleRefresh }
                >
                    <ReplayIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    // The card's answer at a glance: how much the agents ran today, before any scrolling.
    const meta: JSX.Element = (
        <Box sx={ { display: 'flex', alignItems: 'baseline', gap: 1 } }>
            <Typography component="span" sx={ { ...numeric, fontSize: 26, fontWeight: 600, lineHeight: 1 } }>
                { formatTokens(totalItems) }
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
                { totalItems === 1 ? "agent run today" : "agent runs today" }
            </Typography>
        </Box>
    );

    const headCell = { ...eyebrow, borderBottom: 1, borderColor: 'divider', py: 1 };

    return (
        <DashboardCard
            eyebrow="Operator telemetry"
            title="Agent execution times"
            actions={ actions }
            meta={ !loading && error === null ? meta : undefined }
            busy={ loading }
            minHeight={ 380 }
            clip
        >
            { loading &&
                <Skeleton variant="rounded" animation="wave" height={ 280 } />
            }
            { (!loading && error !== null) &&
                <DashboardCardError
                    message={ "Execution times unavailable — " + error }
                    onRetry={ handleRefresh }
                />
            }
            { (!loading && error === null && timings.length === 0) &&
                <Box sx={ { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' } }>
                    <Typography variant="body2" color="text.secondary">
                        { "No agent runs recorded today yet." }
                    </Typography>
                </Box>
            }
            { (!loading && error === null && timings.length > 0) &&
                <TableContainer sx={{ maxHeight: 340 }}>
                    <Table stickyHeader size="small" aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={ { ...headCell, width: 44 } } />
                                <TableCell sx={ { ...headCell, width: 92 } }>{ "Time" }</TableCell>
                                <TableCell sx={ headCell }>{ "Agent" }</TableCell>
                                <TableCell sx={ headCell }>{ "Workflow" }</TableCell>
                                <TableCell sx={ { ...headCell, width: 200 } }>{ "Duration" }</TableCell>
                                <TableCell sx={ { ...headCell, width: 110 } } align="right">{ "Tokens" }</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { timings.map((item: TokenHistoryTiming, index: number) => {
                                return (
                                    <TokenHistoryTimingTableRow row={ item }
                                                                slowest={ slowest }
                                                                key={ "row_item_" + index } />
                                );
                            }) }
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </DashboardCard>
    );
}
